import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';

/**
 * #151 API-Marktplatz-Admin. Konsumiert die CORE-Operator-Routen unter
 * /v1/admin/api-catalog (requireTenant + staff/admin) über den authentifizierten
 * Client (`fetch` hängt Operator-Bearer + Brand-Origin an).
 */

const BASE = '/v1/admin/api-catalog';

export function useApiCatalogList() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['api-catalog', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return { catalog: [] }; }
      return fetch(BASE);
    },
  });
}

export function useApiCatalogUsage(sinceDays = 30) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['api-catalog-usage', sinceDays, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return { sinceDays, apis: [] }; }
      return fetch(`${BASE}/usage?sinceDays=${sinceDays}`);
    },
  });
}

export function useApiCatalogDetail(id) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    enabled: !!id,
    queryKey: ['api-catalog-detail', id, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return { catalog: null }; }
      return fetch(`${BASE}/${id}`);
    },
  });
}

export function useCreateCatalog() {
  return useApiMutation({
    live: (fetch, body) => fetch(BASE, { method: 'POST', body }),
    invalidate: ['api-catalog'],
    successMessage: (res) => `API "${res.catalog?.slug}" angelegt.`,
  });
}

export function useUpdateCatalog() {
  return useApiMutation({
    live: (fetch, { id, patch }) => fetch(`${BASE}/${id}`, { method: 'PATCH', body: patch }),
    invalidate: ['api-catalog', 'api-catalog-detail'],
    successMessage: 'API-Eintrag gespeichert.',
  });
}

export function useDeleteCatalog() {
  return useApiMutation({
    live: (fetch, { id }) => fetch(`${BASE}/${id}`, { method: 'DELETE' }),
    invalidate: ['api-catalog'],
    successMessage: 'API-Eintrag gelöscht.',
  });
}

export function useUpdateTier() {
  return useApiMutation({
    live: (fetch, { productId, patch }) =>
      fetch(`${BASE}/tiers/${productId}`, { method: 'PATCH', body: patch }),
    invalidate: ['api-catalog', 'api-catalog-detail'],
    successMessage: 'Tarif aktualisiert. (Bestehende Abos behalten ihren Snapshot-Preis.)',
  });
}

export function useAddEndpoint() {
  return useApiMutation({
    live: (fetch, { id, body }) => fetch(`${BASE}/${id}/endpoints`, { method: 'POST', body }),
    invalidate: ['api-catalog-detail'],
    successMessage: 'Endpoint hinzugefügt.',
  });
}

export function useDeleteEndpoint() {
  return useApiMutation({
    live: (fetch, { id, eid }) => fetch(`${BASE}/${id}/endpoints/${eid}`, { method: 'DELETE' }),
    invalidate: ['api-catalog-detail'],
    successMessage: 'Endpoint entfernt.',
  });
}
